import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Loader2, Shield, RefreshCw, CheckCircle, XCircle, AlertTriangle, Copy, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function DomainVerificationPage() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [dnsOpen, setDnsOpen] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [stats, setStats] = useState({ ready: 0, warning: 0, blocked: 0, unchecked: 0 });

  const load = async () => {
    try {
      const res = await api.get('/verification/audit');
      const data = res.data;
      setDomains(data);
      setStats({
        ready: data.filter(d => d.mail_ready_status === 'READY').length,
        warning: data.filter(d => d.mail_ready_status === 'WARNING').length,
        blocked: data.filter(d => d.mail_ready_status === 'BLOCKED').length,
        unchecked: data.filter(d => d.mail_ready_status === 'UNCHECKED').length,
      });
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleScanAll = async () => {
    setScanning(true);
    try {
      const res = await api.post('/verification/all');
      toast.success(`Tarama tamamlandi: ${res.data.ready} hazir, ${res.data.warning} uyari, ${res.data.blocked} engelli`);
      load();
    } catch (e) { toast.error('Tarama hatasi'); }
    setScanning(false);
  };

  const handleVerifySingle = async (domainId) => {
    try {
      const res = await api.post(`/verification/domain/${domainId}`);
      toast.success(`${res.data.domain}: ${res.data.status} (${res.data.score}/100)`);
      load();
    } catch (e) { toast.error('Dogrulama hatasi'); }
  };

  const handleShowDNS = async (domainId) => {
    try {
      const res = await api.get(`/verification/dns/${domainId}`);
      setDnsRecords(res.data.records);
      setDnsOpen(res.data.domain);
    } catch (e) { toast.error('DNS alinamadi'); }
  };

  const handleMarkSendgrid = async (domainId, verified) => {
    try {
      await api.post(`/verification/sendgrid-mark/${domainId}`, { verified });
      toast.success(verified ? 'SendGrid dogrulanmis olarak isaretlendi' : 'SendGrid dogrulanmamis isaretlendi');
      load();
    } catch (e) { toast.error('Islem hatasi'); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success('Kopyalandi'); };

  const statusBadge = (s) => {
    if (s === 'PASS') return <Badge className="bg-green-500/20 text-green-400 text-[10px]">PASS</Badge>;
    if (s === 'FAIL') return <Badge className="bg-red-500/20 text-red-400 text-[10px]">FAIL</Badge>;
    if (s === 'WARNING') return <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">UYARI</Badge>;
    return <Badge variant="outline" className="text-[10px]">?</Badge>;
  };

  const readyBadge = (s) => {
    if (s === 'READY') return <Badge className="bg-green-500/20 text-green-400">HAZIR</Badge>;
    if (s === 'WARNING') return <Badge className="bg-amber-500/20 text-amber-400">UYARI</Badge>;
    if (s === 'BLOCKED') return <Badge className="bg-red-500/20 text-red-400">ENGELLI</Badge>;
    return <Badge variant="outline">TARANMADI</Badge>;
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-heading font-bold">Domain Verification Center</h1><p className="text-muted-foreground mt-1">Mail gonderim dogrulama merkezi</p></div>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleScanAll} disabled={scanning}>
            {scanning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Shield className="w-4 h-4 mr-1" />}
            Tum Domainleri Tara
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg bg-card p-4 text-center">
          <CheckCircle className="w-5 h-5 mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold text-green-500">{stats.ready}</p>
          <p className="text-xs text-muted-foreground">Mail Ready</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <AlertTriangle className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold text-amber-500">{stats.warning}</p>
          <p className="text-xs text-muted-foreground">Uyari</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <XCircle className="w-5 h-5 mx-auto text-red-500 mb-1" />
          <p className="text-2xl font-bold text-red-500">{stats.blocked}</p>
          <p className="text-xs text-muted-foreground">Engelli</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Globe className="w-5 h-5 mx-auto text-muted-foreground mb-1" />
          <p className="text-2xl font-bold">{stats.unchecked}</p>
          <p className="text-xs text-muted-foreground">Taranmadi</p>
        </div>
      </div>

      {/* Domain Table */}
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left py-2 px-2">Domain</th>
                <th className="text-center px-1">SPF</th>
                <th className="text-center px-1">DKIM</th>
                <th className="text-center px-1">DMARC</th>
                <th className="text-center px-1">SendGrid</th>
                <th className="text-center px-2">Skor</th>
                <th className="text-center px-2">Durum</th>
                <th className="text-center px-1">Day</th>
                <th className="text-center px-1">Health</th>
                <th className="text-center px-2">Islem</th>
              </tr></thead>
              <tbody>
                {domains.map((d, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-muted/20">
                    <td className="py-2 px-2">
                      <p className="font-medium text-xs">{d.domain}</p>
                      <p className="text-[10px] text-muted-foreground">{d.from_email}</p>
                    </td>
                    <td className="text-center px-1">{statusBadge(d.spf)}</td>
                    <td className="text-center px-1">{statusBadge(d.dkim)}</td>
                    <td className="text-center px-1">{statusBadge(d.dmarc)}</td>
                    <td className="text-center px-1">{statusBadge(d.sendgrid)}</td>
                    <td className="text-center px-2 font-bold">{d.mail_ready_score || '-'}</td>
                    <td className="text-center px-2">{readyBadge(d.mail_ready_status)}</td>
                    <td className="text-center px-1">{d.warmup_day}</td>
                    <td className="text-center px-1">{d.health_score}</td>
                    <td className="text-center px-2">
                      <div className="flex gap-1 justify-center">
                        <Button variant="ghost" size="sm" className="h-6 px-1 text-[10px]" onClick={() => handleVerifySingle(d.domain)}>Tara</Button>
                        <Button variant="ghost" size="sm" className="h-6 px-1 text-[10px]" onClick={() => handleShowDNS(d.domain)}>DNS</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* DNS Dialog */}
      <Dialog open={!!dnsOpen} onOpenChange={() => setDnsOpen(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>DNS Kayitlari — {dnsOpen}</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-xs text-muted-foreground">Eksik kayitlari DNS yoneticinize ekleyin:</p>
            {dnsRecords.map((r, i) => (
              <div key={i} className="p-3 rounded-lg bg-muted/30 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium">{r.name}</span>
                  <Badge variant="outline" className="text-[10px]">{r.type}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs flex-1">Host: {r.host}</code>
                  <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copyText(r.host)}><Copy className="w-3 h-3" /></Button>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs flex-1 text-muted-foreground">Value: {r.value}</code>
                  <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copyText(r.value)}><Copy className="w-3 h-3" /></Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
